import { useCallback } from "react";
import { toast } from "sonner";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../UniversalCRMTable/types";
import type { InlineEditPayload } from "./crudHandlerTypes";

/**
 * Handler for per-row inline edits in the universal CRM table.
 *
 * Caller supplies the dirty fields plus `id` (and optionally the
 * `_isBulkEdit` sentinel) — see {@link InlineEditPayload}.
 */
export function useInlineEditHandler<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>,
  refetch: () => void
) {
  return useCallback(
    async (updatedItem: T) => {
      try {
        // Build minimal input payload: only changed fields.
        // Caller is expected to pass only the dirty fields plus `id` (and
        // optionally the `_isBulkEdit` sentinel) — see InlineEditPayload<T>.
        const { id, _isBulkEdit, ...rest } = updatedItem as unknown as InlineEditPayload<T>;
        const inputPayload: Record<string, unknown> = {
          ...rest,
          updated_at: new Date().toISOString(),
        };

        // DEBUG: Log the input payload
        if (inputPayload.due_date) {
          console.log("[DEBUG] handleInlineEdit inputPayload:", {
            due_date: inputPayload.due_date,
            type: typeof inputPayload.due_date,
          });
        }

        // Set a global flag to indicate we're in a bulk operation
        if (_isBulkEdit) {
          window.__isBulkEditOperation = true;
        }

        const mutationPayload = {
          ...inputPayload,
          id: String(id),
        } as Partial<T> & { id: string };

        await updateMutation.mutateAsync(mutationPayload);

        // Don't show individual success toasts for bulk operations - they're handled by the bulk edit modal
        // Only show toast for single item edits (when not in bulk mode)
        if (!_isBulkEdit) {
          toast.success(`${config.displayName} updated successfully`);
        }

        // Refetch to update the table with the new data
        // This is necessary because the mutation cache may not automatically update the table view
        refetch();
      } catch (error) {
        console.error("Inline edit error:", error);
        toast.error(`Failed to update ${config.displayName}`);
        throw error;
      }
    },
    // NOTE: Preserving original deps (updateMutation, config.displayName) to
    // avoid behavior changes from useCallback invalidation timing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation, config.displayName]
  );
}
